import { Grid, useEventCallback } from '@mui/material';
import { ReprintCidCrubs } from 'front-office/constants/reprint-cid-crubs';
import { Fragment, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Buttons from 'front-office/components/reprint-cid/buttons';
import FormReprintCid from 'front-office/components/reprint-cid/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import {
  ReprintCidFormVM,
  IdentityCardVM,
  SearchPersonVM,
} from 'front-office/view-models/reprint-cid/form';
import { PersonSearchStorageVM, SearchPerson } from 'shell/view-models/search-anpr';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { AxiosResponse } from 'axios';
import { ReprintCid, ReprintCidPerson } from 'shell/models/reprint-cid';
import { DateTime } from 'luxon';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import {
  transformPrintingCardboard,
  transformPrintingEnvelope,
  transformPrintingExternal,
  transformPrintingInterior,
} from 'front-office/utils/transform-request-reprint-cid';
import { toast } from 'react-toastify';
import { transformResidence } from 'front-office/utils/release-id-card-residence-transformer';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { transformerPersonList } from 'shell/utils/transform-person-list';
import { idPaperCard } from 'shell/components/shared/type-indentity-card';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { noop } from 'shell/utils/noop';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import * as yup from 'yup';
import PmiTitle from 'shell/components/shared/title';
import ErrorModalWithHtml from './modal-error';

export type PersonResponse = { body: SearchPersonVM; problems: SaveDataAnpr };

export type VisibilityType = { error?: boolean; warning?: boolean };

const ReprintCidPage = (): React.ReactElement => {
  const [visibility, setVisibility] = useState<VisibilityType>({
    error: undefined,
    warning: undefined,
  });

  const [anprPerson, getAnprPerson] = useHttpFetch<HttpResponse<ReprintCidPerson>, PersonResponse>(
    `${__PERSON_NEW_API_URL__}/ricerca-persona/ristampa-carta`,
    {
      onBootstrap: false,
      transformer: (response: AxiosResponse<HttpResponse<ReprintCidPerson>>) => {
        const [formattedResponse] =
          (response as AxiosResponse<HttpResponse<ReprintCidPerson>>).data?.payload?.risposta3002OK
            ?.listaSoggetti?.datiSoggetto || [];

        const list = transformerPersonList(response)?.persons || [];

        let placeBorn = undefined;

        if (formattedResponse?.generalita?.luogoNascita?.luogoEccezionale) {
          placeBorn = formattedResponse?.generalita?.luogoNascita?.luogoEccezionale || undefined;
        }

        if (formattedResponse?.generalita?.luogoNascita?.comune) {
          placeBorn =
            formattedResponse?.generalita?.luogoNascita?.comune?.denominazione || undefined;
        }

        if (formattedResponse?.generalita?.luogoNascita?.localita) {
          placeBorn =
            formattedResponse?.generalita?.luogoNascita?.localita?.descrizioneLocalita || undefined;
        }

        const body: SearchPersonVM = {
          codicePersonaSipo: formattedResponse?.codicePersonaSipo || '',
          fiscalCode: formattedResponse?.generalita?.codiceFiscale?.codFiscale || '',
          surname: formattedResponse?.generalita?.cognome || '',
          withoutSurname: formattedResponse?.generalita?.cognome ? false : true,
          name: formattedResponse?.generalita?.nome || '',
          withoutName: formattedResponse?.generalita?.nome ? false : true,
          dateContabilizzazione: response.data.payload.registroDocRilasciati?.timestampRilascio
            ? DateTime.fromISO(response.data.payload.registroDocRilasciati?.timestampRilascio)
            : null,
          dateBorn: formattedResponse?.generalita?.dataNascita
            ? DateTime.fromISO(formattedResponse?.generalita?.dataNascita)
            : null,
          placeBorn: placeBorn || undefined,
          numberIdentityCard: formattedResponse?.cartaIdentita?.numero || '',
          dateRelease: formattedResponse?.cartaIdentita?.dataRilascio
            ? DateTime.fromISO(formattedResponse?.cartaIdentita?.dataRilascio)
            : null,
          dateExpiration: formattedResponse?.cartaIdentita?.dataScadenza
            ? DateTime.fromISO(formattedResponse?.cartaIdentita?.dataScadenza)
            : null,
          municipality: formattedResponse?.cartaIdentita?.comuneRilascio?.nomeComune || '',
          province: formattedResponse?.cartaIdentita?.comuneRilascio?.siglaProvinciaIstat || '',
          marriage: formattedResponse?.matrimonio || undefined,
          residence: transformResidence(formattedResponse),
          paternity: formattedResponse?.paternita || undefined,
          maternity: formattedResponse?.maternita || undefined,
          civilUnion: formattedResponse?.unioneCivile || undefined,
          registrationData: formattedResponse?.datiIscrizione || undefined,
          otherDatas: formattedResponse?.altriDati || undefined,
          citizenship: formattedResponse?.cittadinanza
            ? formattedResponse?.cittadinanza[0]
            : undefined,
          // residenceToRequest: formattedResponse?.residenza
          //   ? formattedResponse?.residenza[0]
          //   : undefined,
          actBorn: formattedResponse?.attoNascita || undefined,
          civilStatus: formattedResponse?.statoCivile || undefined,
          gender: formattedResponse?.generalita?.sesso || '',
          documentType:
            formattedResponse?.cartaIdentita?.cartaceaElettronica === '1'
              ? '3'
              : formattedResponse?.cartaIdentita?.cartaceaElettronica || '0',
          isExpatriationBan: formattedResponse?.cartaIdentita?.interdizioneEspatrio,
          registroDocRilasciati:
            (response as AxiosResponse<HttpResponse<ReprintCidPerson>>).data.payload
              .registroDocRilasciati || undefined,
          list,
          generality: formattedResponse?.generalita || undefined,
          residenceToRequest: formattedResponse.residenza,
        };

        if ((response.data.payload?.rispostaKO?.listaErrori?.errore || [])?.length > 0) {
          // mostra errors
          setVisibility({
            error: true,
            warning: false,
          });
        }

        if ((response.data.payload?.risposta3002OK?.listaAnomalie?.errore || [])?.length > 0) {
          // mostra warnings
          setVisibility({
            error: false,
            warning: true,
          });
        }

        return {
          body,
          problems: {
            warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
              (warning) => ({
                id: warning.codiceErroreAnomalia,
                value: warning.testoErroreAnomalia,
              })
            ),
            errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
              id: error.codiceErroreAnomalia,
              value: error.testoErroreAnomalia,
            })),
          },
        } as PersonResponse;
      },
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const castedAnprData = (anprPerson.data || {}) as PersonResponse;

  const [identityCard, getIdentityCard] = useHttpFetch<HttpResponse<ReprintCid>, IdentityCardVM>(
    `${__PERSON_API_URL__}/persona/carte-identita/ristampa/`,
    {
      onBootstrap: false,
      transformer: (response: AxiosResponse<HttpResponse<ReprintCid>>) => {
        const data = response.data?.payload || {};

        const isPaper = data.tipo === idPaperCard;

        const isLess14 =
          DateTime.now().year - ((castedAnprData?.body as SearchPersonVM)?.dateBorn?.year || 0) <=
          14;

        const isExpatriationBan = data.cartaIdentita?.interdizioneEspatrio === 'S';

        const parentsPrint = isPaper && isLess14 && !isExpatriationBan ? 'S' : 'N';

        const responseFormatted: IdentityCardVM = {
          isPaper,
          oldCard: data.cartaPrecedente || '',
          oldDateExpiration: null,
          oldMunicipality: data.comuneRilascioPrecedente || '',
          isValidOld: false,
          isReturnedOld: false,
          number: data.cartaIdentita?.numero,
          type: data.tipo,
          isExpatriationBan,
          dateRelease: data.cartaIdentita?.dataRilascio
            ? DateTime.fromISO(data.cartaIdentita?.dataRilascio)
            : null,
          dateExpiration: data.cartaIdentita?.dataScadenza
            ? DateTime.fromISO(data.cartaIdentita?.dataScadenza)
            : null,
          reasonRelease: data.cartaIdentita?.motivoRilascio || '',
          reasonReleaseFromDB: Boolean(data.cartaIdentita?.motivoRilascio),
          paymentType: data.tipoPagamento || '',
          parentsPrint,
          homeService: data.servizioDomicilio || '',
          municipality: data.cartaIdentita?.descrizioneComuneRilascio || '',
          province: data.cartaIdentita?.descrizioneProvincia || '',
          stature: data.datiFisiciCartaIdentita?.altezza || '',
          hair: data.datiFisiciCartaIdentita?.capelli || '',
          eyes: data.datiFisiciCartaIdentita?.occhi || '',
          particularSigns: data.datiFisiciCartaIdentita?.note || '',
          fixedRights: data.modalitaRilascio?.importoDirittiFissi
            ? Number.parseFloat(data.modalitaRilascio?.importoDirittiFissi)
            : 0,
          secretarialRights: data.modalitaRilascio?.importoDirittiSegreteria
            ? Number.parseFloat(data.modalitaRilascio?.importoDirittiSegreteria)
            : 0,
        };

        return responseFormatted;
      },
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
    }
  );

  const navigate = useNavigate();

  const handleExit = useEventCallback(() => {
    navigate('/sportello');
  });

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  useEffect(() => {
    searchPerson();
  }, [nationalSearch, fiscalCode]);

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const searchPerson = useEventCallback(() => {
    const data: SearchPerson = {
      datiRichiesta: {
        ricercaNazionale: nationalSearch ? '1' : '0',

        dataRiferimentoRichiesta:
          filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
          undefined,
        comuneResidenza:
          filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
          filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
          filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
          filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
            ? {
                siglaProvinciaIstat:
                  filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.province ||
                  filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.province ||
                  undefined,
                codiceIstat:
                  filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                  filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                  undefined,
                nomeComune:
                  filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                    ?.description ||
                  filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.description ||
                  undefined,
              }
            : undefined,
      },
      criteriRicerca: {
        codiceFiscale: fiscalCode || undefined,
        idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
      },
    };

    (
      getAnprPerson({ data }) as ResponsePromise<HttpResponse<ReprintCidPerson>, SearchPersonVM>
    ).then((response) => {
      const formattedResponse = response as SearchPersonVM;
      if ((formattedResponse?.list || []).length > 1) {
        // più risultati per la ricerca
        return;
      }
      if (formattedResponse?.numberIdentityCard && formattedResponse?.isExpatriationBan) {
        getIdentityCard({
          url: `${formattedResponse?.numberIdentityCard || ''}-${
            formattedResponse?.isExpatriationBan || ''
          }`,
        });
      }
    });
  });

  const printingInteriorCall = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/lato-interno`,
    {
      transformer: (response) => {
        return response.data;
      },
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const printingInterior = useEventCallback((values: ReprintCidFormVM) => {
    const data = transformPrintingInterior(values || {});

    (printingInteriorCall({ data }) as ResponsePromise<void>).then((response) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pdfCertificate = (response as any)?.payload?.contenutoPdf;

      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({
          data: pdfCertificate,
          name: 'Stampa Interno',
          open: true,
        });
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  const printingExternalCall = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/lato-esterno`,
    {
      transformer: (response) => {
        return response.data;
      },
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const printingExternal = useEventCallback((values: ReprintCidFormVM) => {
    const data = transformPrintingExternal(values || {});

    (printingExternalCall({ data }) as ResponsePromise<void>).then((response) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pdfCertificate = (response as any)?.payload?.contenutoPdf;

      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({
          data: pdfCertificate,
          name: 'Stampa Esterno',
          open: true,
        });
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  const printingCardboardCall = useHttpClient<
    HttpResponse<{ contenutoPdf: string }>,
    HttpResponse<{ contenutoPdf: string }>
  >(`${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/cartellino`, {
    transformer: (response) => {
      return response.data;
    },
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
  });

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const printingCardboard = useEventCallback((values: ReprintCidFormVM) => {
    const data = transformPrintingCardboard(values || {});

    (
      printingCardboardCall({ data: { stampaCartellino: data } }) as ResponsePromise<
        HttpResponse<{ contenutoPdf: string }>,
        HttpResponse<{ contenutoPdf: string }>
      >
    ).then((response) => {
      const pdfCertificate = (response as HttpResponse<{ contenutoPdf: string }>)?.payload
        ?.contenutoPdf;

      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({
          data: pdfCertificate,
          name: 'Stampa Cartellino',
          open: true,
        });
      }

      toast.success('Operazione eseguita con successo');
    });
  });

  const printingEnvelopeCall = useHttpClient<HttpResponse<Blob>>(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/ristampa/busta`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const checkPrintingEnvelopeCall = useHttpClient<HttpResponse<{ warning: string[] }>>(
    `${__PERSON_NEW_API_URL__}/persona/carta-identita/stampa/ristampa/controlli`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const [warningModal, setWarningModal] = useState<{ open: boolean; warning?: string }>({
    open: false,
  });

  const checkPrintingEnvelope = useEventCallback((values: ReprintCidFormVM) => {
    const data = transformPrintingEnvelope(values || {});

    (
      checkPrintingEnvelopeCall({ data }) as ResponsePromise<HttpResponse<{ warning: string[] }>>
    ).then((response) => {
      if (response?.data?.payload?.warning && response?.data?.payload?.warning?.length > 0) {
        setWarningModal({
          open: true,
          warning: response?.data.payload?.warning.join('\n'),
        });

        return;
      }

      printingEnvelope(values);
    });
  });

  const printingEnvelope = useEventCallback((values: ReprintCidFormVM) => {
    const data = transformPrintingEnvelope(values || {});

    (printingEnvelopeCall({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
      (response) => {
        if (response?.data) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Stampa Busta',
              open: true,
            });
          });
        }
        toast.success('Operazione eseguita con successo');
      }
    );
  });

  const identityCardVM = useMemo(() => {
    const anprPersonData = castedAnprData?.body as SearchPersonVM;

    return {
      ...(identityCard.data || {}),
      number: anprPersonData?.numberIdentityCard,
      isExpatriationBan: anprPersonData?.isExpatriationBan === 'S',
      type: anprPersonData?.documentType,
      paymentType: anprPersonData?.registroDocRilasciati?.tipoPagamento,
      dateContabilizzazione: anprPersonData?.dateContabilizzazione || undefined,
      dateRelease: anprPersonData?.dateRelease || undefined,
      dateExpiration: anprPersonData?.dateExpiration || undefined,
      municipality: anprPersonData?.municipality || undefined,
      province: anprPersonData?.province || undefined,
    };
  }, [identityCard.data, anprPerson.data]);

  return (
    <Fragment>
      <Breadcrumbs items={ReprintCidCrubs} base="sportello" />
      <PmiTitle title="Stampa busta CIE" paddingBottom={1} />
      <Grid container spacing={1}>
        <Grid item xs={12} md={2}>
          <Buttons handleExit={handleExit} />
        </Grid>
        <Grid item xs={12} md={10}>
          <Formik
            initialValues={{
              identityCard: identityCardVM as IdentityCardVM,
            }}
            onSubmit={noop}
            validationSchema={yup.object().shape({
              identityCard: yup.object().shape({
                dateContabilizzazione: yup
                  .date()
                  .typeError('Data non valida')
                  .required('Campo obbligatorio'),
                reasonRelease: yup.string().required('Campo obbligatorio'),
                paymentType: yup.string().required('Campo obbligatorio'),
              }),
            })}
          >
            {({ values }) => (
              <Form>
                <FormReprintCid
                  initialValuesIdentityCard={identityCardVM as IdentityCardVM}
                  initialValuesPerson={castedAnprData?.body}
                  printingInterior={printingInterior}
                  printingExternal={printingExternal}
                  printingCardboard={printingCardboard}
                  printingEnvelope={checkPrintingEnvelope}
                  castedAnprData={castedAnprData}
                  visibility={visibility}
                  setVisibility={setVisibility}
                />
                {warningModal.open ? (
                  <ErrorModalWithHtml
                    open={warningModal.open}
                    onClose={() => {
                      setWarningModal({ open: false });
                    }}
                    onConfirm={() => {
                      setWarningModal({ open: false });
                      printingEnvelope(values);
                    }}
                    message={warningModal.warning || ''}
                  />
                ) : undefined}
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>

      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title={pdfModal.name}
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
          navigate('/sportello');
        }}
      />
    </Fragment>
  );
};

ReprintCidPage.displayName = 'Reprint CID Page';

export default ReprintCidPage;
